/* eslint-disable no-console */
/* eslint-disable indent */
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { useMeta } from '../context/metaContext';

import ErrorBoundary from '@/components/errorBoundary';
import { FullPageSpinner } from '@/components/Loader';
import { fetchPaymentLink } from '../lib/requests';
import { computeRecurringMeta } from '../utils/enums';
import { getQueryParams } from '../utils/query-params';

const ProductPayment = lazy(() => import('../pages/ProductPayment'));
const OneTimePayment = lazy(() => import('../pages/OneTimePayment'));
const Donation = lazy(() => import('../pages/Donation'));
const Recurring = lazy(() => import('../pages/Recurring'));
const InvalidLink = lazy(() => import('../pages/InvalidLink'));
const ExpiredLink = lazy(() => import('../pages/ExpiredLink'));
const InactiveLink = lazy(() => import('../pages/InactiveLink'));
// const InvoiceSample2 = lazy(() => import("./pages/Invoice/Samples/Sample2"));
// const InvoiceSample3 = lazy(() => import("./pages/Invoice/Samples/Sample3"));

function PaymentLinkHome() {
  const router = useRouter();
  const currentUrl = router.asPath;

  const { metaValues, setMetaValues, setIsLoading, extraValues } = useMeta();

  const [publicKey, setPublicKey] = React.useState('');
  const [linkDetails, setLinkDetails] = React.useState(null);
  const [logoUrl, setLogoUrl] = React.useState('');
  const [isLinkValid, setIsLinkValid] = React.useState(true);
  const [isLinkInactive, setIsLinkInactive] = React.useState(false);
  const [isLinkExpired, setIsLinkExpired] = React.useState(false);
  const [linkId, setLinkId] = useState();
  const [linkHasHash, setLinkHasHash] = useState(null);

  function initWidget(params, publicKey) {
    const squad = window.squad;

    if (Object.keys(extraValues).length > 0) {
      params.extra = extraValues;
    }

    let oldMeta = { ...getQueryParams() };
    if (params.extra) oldMeta.extra = JSON.stringify(params.extra);
    // params.currency_id = generateCurrencyId(params.currency_code);
    params.redirect_link = metaValues?.redirectLink;
    const squadInstance = new squad({
      onClose: () => {
        console.log('Widget closed');
        setIsLoading(false);
      },
      onLoad: () => {
        console.log('Widget loaded successfully');
        setIsLoading(false);
      },
      onSuccess: () => {},
      key: publicKey,
      ...params,
      metadata: oldMeta,
    });

    squadInstance.setup();
    squadInstance.open();
  }

  function loadPage(data) {
    // check if link isactive
    if (data.link_details.link_status === 0) {
      setIsLinkInactive(true);
    }

    // check if link is expired
    if (data?.link_details?.expire_by && new Date(data?.link_details?.expire_by) < new Date()) {
      setIsLinkExpired(true);
    }

    // check is donation start date has reached
    if (
      data?.link_details?.link_type === 'donation' &&
      new Date(data?.link_details?.link_type?.meta?.start_date) < new Date()
    ) {
      setIsLinkInactive(true);
    }

    setPublicKey(data.devkit);
    setLinkDetails(data.link_details ?? '');
    if (data.category && Object.keys(data.category).length > 0) {
      setLinkDetails(prevDetails => ({
        ...prevDetails,
        category: data.category
      }));
    }

    setLogoUrl(data?.logo?.url ?? '');
    const { link_details: metaData } = data;

    const { frequency, duration } = computeRecurringMeta(metaData?.meta?.interval, metaData?.meta?.billing_cycle);

    const recurring = {
      duration: String(duration),
      frequency: String(frequency),
      plan_code: metaData?.hash,
    };

    // store the meta details for use later
    if (metaData) {
      setMetaValues({
        name: metaData?.name,
        supportEmail: metaData?.support_email,
        supportPhone: metaData?.support_phone,
        terms: metaData?.terms_condition,
        returnMessage: metaData?.return_msg,
        hash: metaData?.hash,
        returnPolicy: metaData?.return_policy,
        redirectLink: metaData?.redirect_link,
        linkType: metaData?.link_type,
        description: metaData?.description,
        pickup_location: metaData?.pickup_location,
        merchantId: metaData?.merchant_id,
        verified: data?.verified ?? false,
        storeLogo: data?.logo?.url,
        storeName: data?.name,
        display_name: data?.display_name,
        invoiceDetails: metaData?.meta,
        dueDate: metaData?.expire_by,
        recurring,
        extra: data?.link_details?.extra,
      });
    }
  }

  useEffect(() => {
    if (router?.query?.link !== undefined) {
      const path = currentUrl?.toString();
      setLinkId(path.split('/').pop());

      const lastSlashIndex = path.lastIndexOf('/');
      const substringAfterLastSlash = path.substring(lastSlashIndex + 1);

      const hasCharactersAfterLastSlash = substringAfterLastSlash.length < path.length;

      setLinkHasHash(hasCharactersAfterLastSlash);
    }
  }, []);

  useEffect(() => {
    if (router?.query?.link !== undefined) {
      const path = currentUrl?.toString();
      setLinkId(path.split('/').pop());

      const lastSlashIndex = path.lastIndexOf('/');

      const substringAfterLastSlash = path.substring(lastSlashIndex + 1);

      const hasCharactersAfterLastSlash = substringAfterLastSlash.length < path.length;

      if (hasCharactersAfterLastSlash) {
        const _pathname = currentUrl.toString();
        const hash = _pathname.split('/').pop();

        if (window.payload && window.payload.link_details) {
          loadPage(window.payload);
        } else {
          fetchPaymentLink(hash)
            .then((res) => {
              if (res.status === 200) {
                // check if link isactive
                loadPage(res.data.data);
              } else {
                setIsLinkValid(false);
              }
            })
            .catch(() => {
              setIsLinkValid(false);
            });
        }
      } else {
        setIsLinkValid(false);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router?.query?.link]);

  return (
    <ErrorBoundary>
      {linkHasHash ? (
        <RenderView
          initWidget={initWidget}
          linkDetails={linkDetails}
          logoUrl={logoUrl}
          isLinkValid={isLinkValid}
          publicKey={publicKey}
          isLinkInactive={isLinkInactive}
          isLinkExpired={isLinkExpired}
        />
      ) : (
        <RenderView
          initWidget={initWidget}
          linkDetails={linkDetails}
          logoUrl={logoUrl}
          isLinkValid={isLinkValid}
          publicKey={publicKey}
          isLinkInactive={isLinkInactive}
          isLinkExpired={isLinkExpired}
        />
      )}
    </ErrorBoundary>
  );
}

export default PaymentLinkHome;

function RenderView({ initWidget, linkDetails, isLinkValid, publicKey, isLinkInactive, isLinkExpired, logoUrl }) {
  return (
    <>
      {isLinkExpired ? (
        <ExpiredLink />
      ) : isLinkInactive ? (
        <InactiveLink />
      ) : linkDetails?.link_type === 'otp' ? (
        <OneTimePayment initWidget={initWidget} publicKey={publicKey} data={linkDetails} logo={logoUrl} />
      ) : linkDetails?.link_type === 'recurring' ? (
        <Recurring initWidget={initWidget} publicKey={publicKey} data={linkDetails} logo={logoUrl} />
      ) : linkDetails?.link_type === 'donation' ? (
        <Donation initWidget={initWidget} publicKey={publicKey} data={linkDetails} logo={logoUrl} />
      ) : linkDetails?.link_type === 'product' ? (
        <ProductPayment initWidget={initWidget} publicKey={publicKey} data={linkDetails} logo={logoUrl} />
      ) : !isLinkValid ? (
        <InvalidLink />
      ) : (
        <FullPageSpinner />
      )}
    </>
  );
}
